import { type personalizedCollectionsTracking_item$key } from 'dibs-personalized/exports/tracking.ts';
import { type HpSharedItemsGridContent_item$key } from './components/__generated__/HpSharedItemsGridContent_item.graphql.ts';
import { type hpSharedItemsGridTracking_item$key } from './__generated__/hpSharedItemsGridTracking_item.graphql.ts';
import { type HpSharedItemsGridPersonalizedCollection_item$key } from './components/__generated__/HpSharedItemsGridPersonalizedCollection_item.graphql.ts';

type HpSharedItemsGridRegularItemRef = HpSharedItemsGridContent_item$key &
    hpSharedItemsGridTracking_item$key;

export const HP_SHARED_ITEMS_GRID_ITEM_TYPES = {
    EDITORS_PICKS: 'editorsPicks',
    MOST_SAVED: 'mostSaved',
    EXCELLENT_VALUES: 'excellentValues',
    BESTSELLING_DESIGNS: 'bestsellingDesigns',
    FEATURED_FOR_YOU: 'featuredForYou',
    USER_RECOMMENDATIONS: 'userRecommendations',
    PERSONALIZED_COLLECTION: 'personalizedCollection',
    EDITORIAL_COLLECTION: 'editorialCollection',
} as const;

type HpSharedItemsGridItemType =
    (typeof HP_SHARED_ITEMS_GRID_ITEM_TYPES)[keyof typeof HP_SHARED_ITEMS_GRID_ITEM_TYPES];

export type HpSharedItemsGridRegularItem = {
    type: Exclude<
        HpSharedItemsGridItemType,
        | typeof HP_SHARED_ITEMS_GRID_ITEM_TYPES.PERSONALIZED_COLLECTION
        | typeof HP_SHARED_ITEMS_GRID_ITEM_TYPES.EDITORIAL_COLLECTION
    >;
    itemId: string;
    itemRef: HpSharedItemsGridRegularItemRef;
};

export type HpSharedItemsGridPersonalizedCollectionNestedItem = {
    itemId: string;
    itemRef: HpSharedItemsGridPersonalizedCollection_item$key &
        personalizedCollectionsTracking_item$key;
};

export type HpSharedItemsGridPersonalizedCollectionItem = {
    type: typeof HP_SHARED_ITEMS_GRID_ITEM_TYPES.PERSONALIZED_COLLECTION;
    collectionId: string;
    collectionName: string | null;
    catalogType: string | null;
    collectionPositionIndex: number;
    items: HpSharedItemsGridPersonalizedCollectionNestedItem[];
    isEmpty?: boolean;
    seedItemId?: string | null;
};

export type HpSharedItemsGridEditorialCollectionItem = {
    type: typeof HP_SHARED_ITEMS_GRID_ITEM_TYPES.EDITORIAL_COLLECTION;
    cta: string;
    header: string | null;
    imageUrl: string;
    title: string;
    linkPath: string;
    editorialModuleOrder: number;
    editorialItemIndex: number;
    isEmpty?: boolean;
};

export type HpSharedItemsGridCollectionItem =
    | HpSharedItemsGridPersonalizedCollectionItem
    | HpSharedItemsGridEditorialCollectionItem;

type HpSharedItemsGridFeaturedForYouItem = HpSharedItemsGridRegularItem & {
    type: typeof HP_SHARED_ITEMS_GRID_ITEM_TYPES.FEATURED_FOR_YOU;
};

export type HpSharedItemsGridItem = HpSharedItemsGridRegularItem | HpSharedItemsGridCollectionItem;

export type HpSharedItemsGridData = {
    regularItems: HpSharedItemsGridRegularItem[];
    fallbackItems: HpSharedItemsGridRegularItem[];
    personalizedCollectionItems: HpSharedItemsGridPersonalizedCollectionItem[];
    editorialCollectionItems: HpSharedItemsGridEditorialCollectionItem[];
};

export function isPersonalizedCollectionItem(
    item: HpSharedItemsGridItem
): item is HpSharedItemsGridPersonalizedCollectionItem {
    return item.type === HP_SHARED_ITEMS_GRID_ITEM_TYPES.PERSONALIZED_COLLECTION;
}

export function isEditorialCollectionItem(
    item: HpSharedItemsGridItem
): item is HpSharedItemsGridEditorialCollectionItem {
    return item.type === HP_SHARED_ITEMS_GRID_ITEM_TYPES.EDITORIAL_COLLECTION;
}

export function isCollectionItem(
    item: HpSharedItemsGridItem
): item is HpSharedItemsGridCollectionItem {
    return isPersonalizedCollectionItem(item) || isEditorialCollectionItem(item);
}

export function isFeaturedForYouItem(
    item: HpSharedItemsGridItem
): item is HpSharedItemsGridFeaturedForYouItem {
    return item.type === HP_SHARED_ITEMS_GRID_ITEM_TYPES.FEATURED_FOR_YOU;
}

export function isRegularItem(item: HpSharedItemsGridItem): item is HpSharedItemsGridRegularItem {
    return !isCollectionItem(item);
}
